import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { U3129_HONORARIUM_TEMPLATE_CHANGED_EVENT } from '../u3129Timesheet/tools/u3129HonorariumTemplate';
import { useU2000AuthContext } from '../u2000Core/context/auth/U2000UseAuthContext';
import {
  u3147ChipCanFetchTasksForEnrich,
  u3147EnrichRecentChipFromTaskList,
  u3147RecentProjectId,
  u3147ResolveTaskIdForRecentApply,
  type U3147FindProjectItem,
  type U3147FindSubprojectItem,
  type U3147RecentProjectItem,
  type U3147TaskItem,
} from './U3147Service';
import { useU3147FindTasksForProjectHandlers } from './U3147UseFindProjectSubprojectHandlers';
import {
  getHonorariumTemplateStorageKey,
  u3147FormRowIdentityKey,
  u3147PatchHonorariumTemplateRowTaskMetadata,
  u3147ReadHonorariumTemplateRecentProjects,
  u3147RecentProjectChipKey,
  u3147RemoveHonorariumTemplateRowByKey,
  u3147TimerFormToTemplateRow,
  u3147UpdateHonorariumTemplateRowNote,
  u3147UpsertHonorariumTemplateRow,
} from './tools/u3147HonorariumTemplateSync';

export function useU3147HonorariumTemplateSync(lang: string) {
  const { auth } = useU2000AuthContext();
  const { fetchTasks } = useU3147FindTasksForProjectHandlers();
  const fetchTasksRef = useRef(fetchTasks);
  fetchTasksRef.current = fetchTasks;
  const [templateRevision, setTemplateRevision] = useState(0);
  const [enrichedByChipKey, setEnrichedByChipKey] = useState<Record<string, U3147RecentProjectItem>>({});

  const storageKey = useMemo(
    () =>
      auth.isAuthenticated && auth.user.userName
        ? getHonorariumTemplateStorageKey(auth.user.userName, auth.user.organizationId || 'noorg')
        : 'U3129_guest_honorariumTemplate',
    [auth.isAuthenticated, auth.user.organizationId, auth.user.userName],
  );

  const reloadTemplate = useCallback(() => {
    setTemplateRevision((n) => n + 1);
  }, []);

  useEffect(() => {
    const onStorage = (event: StorageEvent) => {
      if (event.key === storageKey) reloadTemplate();
    };
    const onCustom = (event: Event) => {
      const detail = (event as CustomEvent<{ storageKey?: string }>).detail;
      if (!detail?.storageKey || detail.storageKey === storageKey) reloadTemplate();
    };
    window.addEventListener('storage', onStorage);
    window.addEventListener(U3129_HONORARIUM_TEMPLATE_CHANGED_EVENT, onCustom);
    return () => {
      window.removeEventListener('storage', onStorage);
      window.removeEventListener(U3129_HONORARIUM_TEMPLATE_CHANGED_EVENT, onCustom);
    };
  }, [storageKey, reloadTemplate]);

  const favoriteProjects = useMemo(() => {
    void templateRevision;
    return u3147ReadHonorariumTemplateRecentProjects(storageKey);
  }, [storageKey, templateRevision]);

  const favoriteProjectsKey = useMemo(
    () => favoriteProjects.map((chip) => u3147RecentProjectChipKey(chip)).join('\n'),
    [favoriteProjects],
  );

  useEffect(() => {
    setEnrichedByChipKey({});
  }, [favoriteProjectsKey]);

  useEffect(() => {
    const needsEnrich = favoriteProjects.some((chip) => u3147ChipCanFetchTasksForEnrich(chip));
    if (!needsEnrich) return undefined;

    let cancelled = false;

    void (async () => {
      const fetchKeys = new Map<string, { projectId: string; subprojectId: string | null }>();
      for (const chip of favoriteProjects) {
        if (!u3147ChipCanFetchTasksForEnrich(chip)) continue;
        const projectId = u3147RecentProjectId(chip);
        if (!projectId) continue;
        const subprojectId = chip.subprojectId?.trim() || null;
        fetchKeys.set(`${projectId}|${subprojectId ?? ''}`, { projectId, subprojectId });
      }

      const taskLists = new Map<string, U3147TaskItem[]>();
      await Promise.all(
        [...fetchKeys.entries()].map(async ([key, body]) => {
          const list = await fetchTasksRef.current(body);
          taskLists.set(key, list);
        }),
      );

      if (cancelled) return;

      const next: Record<string, U3147RecentProjectItem> = {};
      for (const chip of favoriteProjects) {
        if (!u3147ChipCanFetchTasksForEnrich(chip)) continue;
        const projectId = u3147RecentProjectId(chip);
        if (!projectId) continue;
        const subprojectId = chip.subprojectId?.trim() || null;
        const list = taskLists.get(`${projectId}|${subprojectId ?? ''}`) ?? [];
        const enriched = u3147EnrichRecentChipFromTaskList(chip, list);
        if (enriched !== chip) {
          next[u3147RecentProjectChipKey(chip)] = enriched;
        }
      }

      if (Object.keys(next).length > 0) {
        setEnrichedByChipKey((prev) => ({ ...prev, ...next }));
        for (const [chipKey, enriched] of Object.entries(next)) {
          const taskListKey = `${u3147RecentProjectId(enriched)}|${enriched.subprojectId?.trim() ?? ''}`;
          const list = taskLists.get(taskListKey) ?? [];
          const resolvedId = u3147ResolveTaskIdForRecentApply(enriched, list);
          const task = list.find((entry) => String(entry.id) === String(resolvedId));
          if (task) {
            u3147PatchHonorariumTemplateRowTaskMetadata(storageKey, chipKey, task, lang);
          }
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [favoriteProjects, favoriteProjectsKey, storageKey, lang]);

  const displayFavoriteProjects = useMemo(
    () =>
      favoriteProjects.map((chip) => {
        const enriched = enrichedByChipKey[u3147RecentProjectChipKey(chip)];
        return enriched ?? chip;
      }),
    [favoriteProjects, enrichedByChipKey],
  );

  const isFormRowFavorite = useCallback(
    (data: {
      project: U3147FindProjectItem | null;
      subproject: U3147FindSubprojectItem | null;
      taskId: string;
    }): boolean => {
      const key = u3147FormRowIdentityKey(data);
      if (!key) return false;
      return favoriteProjects.some((item) => u3147RecentProjectChipKey(item) === key);
    },
    [favoriteProjects],
  );

  const addFavorite = useCallback(
    (data: {
      project: U3147FindProjectItem | null;
      subproject: U3147FindSubprojectItem | null;
      taskId: string;
      note?: string;
      task?: U3147TaskItem | null;
    }): boolean => {
      const row = u3147TimerFormToTemplateRow({ ...data, lang });
      if (!row) return false;
      const ok = u3147UpsertHonorariumTemplateRow(storageKey, row);
      if (ok) reloadTemplate();
      return ok;
    },
    [storageKey, reloadTemplate, lang],
  );

  const removeFavoriteByKey = useCallback(
    (identityKey: string): boolean => {
      const ok = u3147RemoveHonorariumTemplateRowByKey(storageKey, identityKey);
      if (ok) reloadTemplate();
      return ok;
    },
    [storageKey, reloadTemplate],
  );

  const removeFavoriteForForm = useCallback(
    (data: {
      project: U3147FindProjectItem | null;
      subproject: U3147FindSubprojectItem | null;
      taskId: string;
    }): boolean => {
      const key = u3147FormRowIdentityKey(data);
      if (!key) return false;
      return removeFavoriteByKey(key);
    },
    [removeFavoriteByKey],
  );

  const syncFavoriteNote = useCallback(
    (data: {
      project: U3147FindProjectItem | null;
      subproject: U3147FindSubprojectItem | null;
      taskId: string;
      note?: string;
    }): boolean => {
      const key = u3147FormRowIdentityKey(data);
      if (!key || !favoriteProjects.some((item) => u3147RecentProjectChipKey(item) === key)) return false;
      const ok = u3147UpdateHonorariumTemplateRowNote(storageKey, key, data.note);
      if (ok) reloadTemplate();
      return ok;
    },
    [storageKey, reloadTemplate],
  );

  return {
    storageKey,
    favoriteProjects: displayFavoriteProjects,
    isFormRowFavorite,
    addFavorite,
    removeFavoriteByKey,
    removeFavoriteForForm,
    syncFavoriteNote,
    reloadTemplate,
  };
}
